import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { style } from './video.style.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { CSEIconButton } from 'elements/main.js'

initializeIconsByRole({
  'cse-video.play': { name: 'play', style: 'light' }
})

export interface VideoSource {
  src: string
  type: string
}

@customElement('cse-video')
export class CSEVideo extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @query('video')
  private videoElement?: HTMLVideoElement

  @query('.play-button')
  private playButton?: CSEIconButton

  @property({ type: Array })
  sources: VideoSource[] = []

  @property({ type: String, attribute: 'embed-src' })
  embedSrc = ''

  @property({ type: String })
  override title = ''

  @property({ type: String })
  height: number | 'auto' = 'auto'

  @property({ type: String })
  width: number | 'auto' = 'auto'

  @property({ type: Boolean })
  autoplay = false

  private get isEmbedMode(): boolean {
    return this.embedSrc.length > 0
  }

  override firstUpdated() {
    if (this.autoplay && !this.isEmbedMode) {
      this.startNativePlayback()
    }
  }

  override updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties)
    if (changedProperties.has('autoplay') && this.autoplay && !this.isEmbedMode) {
      this.startNativePlayback()
    }
  }

  private startNativePlayback() {
    if (!this.videoElement) {
      return
    }

    const playResult = this.videoElement.play() ?? Promise.resolve()
    this.videoElement.setAttribute('controls', '')
    this.playButton?.remove()

    playResult.catch((error) => {
      console.error(`Video playback failed: ${error}`)
    })
  }

  private onPlayClick() {
    this.startNativePlayback()
  }

  override render() {
    if (this.isEmbedMode) {
      return this.renderEmbed()
    }

    return this.renderNativeVideo()
  }

  /** YouTube/Vimeo: load iframe immediately so the provider preview UI is shown. */
  private renderEmbed() {
    return html`
      <div class="cse-video cse-video--embed">
        <iframe
          src="${this.embedSrc}"
          title="${this.title}"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen
        ></iframe>
      </div>
    `
  }

  private renderNativeVideo() {
    const showPlayButton = !this.autoplay

    return html`
      <div class="cse-video">
        <video width="${this.width}" height="${this.height}" title="${this.title}" ?autoplay="${this.autoplay}">
          ${this.sources.map(source => html`
            <source src="${source.src}" type="${source.type}">
          `)}
          Your browser doesn't support HTML video
        </video>
        ${showPlayButton ? html`
          <cse-icon-button class="play-button" @click="${this.onPlayClick}">
            <cse-icon icon-role="cse-video.play"></cse-icon>
          </cse-icon-button>
        ` : nothing}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cse-video': CSEVideo
  }
}
